import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { TFunction } from 'next-i18next';
import {
  Avatar,
  Button,
  ButtonVariant,
  CommentIcon,
  Icon,
  Input,
  InputVariant,
  ModalBody,
  ModalCustom,
  ModalFooter,
  ModalHeader,
} from '@/components';
import { VideoReelCommentModel, VideoReelReplyCommentModel } from '@/models';
import {
  useDeleteComment,
  useDeleteReplyComment,
  useFetchReplyCommentsQuery,
  useUpdateComment,
  useUpdateReplyComment,
} from '../api';

interface CommentProps {
  comment: VideoReelCommentModel | VideoReelReplyCommentModel;
  isChild?: boolean;
  isShowReplyInput: boolean;
  isAuth: boolean;
  profile: any;
  replyCommentContent: string;
  setReplyCommentContent: (value: string) => void;
  setReplyCommentInput: (value: any) => void;
  editCommentInput: any;
  setEditCommentInput: (value: any) => void;
  editCommentContent: string;
  setEditCommentContent: (value: string) => void;
  onShowReplyInput: (
    comment: VideoReelCommentModel | VideoReelReplyCommentModel
  ) => void;
  onSetReplies: (
    commentId: number,
    replies: VideoReelReplyCommentModel[]
  ) => void;
  onSendReplyComment: () => void;
  t: TFunction;
}

const Comment = (props: CommentProps) => {
  const {
    comment,
    isChild = false,
    isShowReplyInput,
    replyCommentContent,
    setReplyCommentContent,
    setReplyCommentInput,
    editCommentInput,
    setEditCommentInput,
    editCommentContent,
    setEditCommentContent,
    onShowReplyInput,
    onSetReplies,
    onSendReplyComment,
    t,
    profile,
    isAuth,
  } = props;

  const [isExpandingReplies, setIsExpandingReplies] = useState<boolean>(false);
  const [commentToExpand, setCommentToExpand] =
    useState<VideoReelCommentModel | null>(null);
  const [isShowConfirmDelete, setIsShowConfirmDelete] =
    useState<boolean>(false);

  const { refetch: fetchReplyComments, isLoading: isLoadingReplyComments } =
    useFetchReplyCommentsQuery(
      {
        commentId: Number(commentToExpand?.id),
        params: { limit: commentToExpand?.reply_comment_count },
      },
      {
        enabled: false,
        refetchOnWindowFocus: false,
        onError: ({ message }) => {
          toast.error(message);
        },
        onSuccess: ({ data, total_records }) => {
          const replies = Array.isArray(data) ? data : [];
          onSetReplies(Number(commentToExpand?.id), replies);
        },
      }
    );

  const { mutate: deleteComment, isLoading: isDeletingComment } =
    useDeleteComment();

  const { mutate: deleteReplyComment, isLoading: isDeletingReplyComment } =
    useDeleteReplyComment();

  const { mutate: updateComment, isLoading: isUpdatingComment } =
    useUpdateComment();

  const { mutate: updateReplyComment, isLoading: isUpdatingReplyComment } =
    useUpdateReplyComment();

  useEffect(() => {
    if (commentToExpand?.id) {
      fetchReplyComments();
    }
  }, [commentToExpand, fetchReplyComments]);

  const handleToggleShowReply = (comment: VideoReelCommentModel) => {
    if (isExpandingReplies) {
      setCommentToExpand(null);
      setIsExpandingReplies(false);
      if (isShowReplyInput) {
        setReplyCommentInput(null);
      }
    } else {
      setCommentToExpand(comment);
      setIsExpandingReplies(true);
    }
  };

  const handleKeyPress = (e: any) => {
    if (e.keyCode === 13) {
      onSendReplyComment();
    }
  };

  const handleEditKeyPress = (e: any) => {
    if (e.keyCode === 13) {
      handleEditComment();
    }
  };

  const handleDeleteComment = () => {
    if (isChild) {
      deleteReplyComment(comment.id);
    } else {
      deleteComment(comment.id);
    }
    setIsShowConfirmDelete(false);
  };

  const handleEditComment = () => {
    if (isChild) {
      updateReplyComment({
        commentId: editCommentInput.commentId,
        body: { comment: editCommentContent },
      });
    } else {
      updateComment({
        commentId: editCommentInput.commentId,
        body: { comment: editCommentContent },
      });
    }
    setEditCommentInput(null);
  };

  const showEditComment = () => {
    setEditCommentInput({ commentId: comment.id });
    setEditCommentContent(comment.comment);
  };

  const isOwner = () => {
    return profile && profile.id === comment.users?.id;
  };

  const isEdit = () => {
    return Boolean(editCommentInput?.commentId === comment.id);
  };

  return (
    <>
      <div className={`${isChild ? 'tw-mb-2' : 'tw-mb-5'}`}>
        <div className="tw-flex tw-items-start tw-gap-2 tw-mb-2">
          <Avatar
            src={comment.users?.avatar || comment.staffs?.avatar || ''}
            className="tw-w-8 tw-h-8"
            rounded="tw-rounded-full"
          />
          {isEdit() ? (
            <div className="tw-flex-grow">
              <Input
                containerClassName="tw-rounded-full tw-px-4"
                className="tw-text-sm"
                variant={InputVariant.Outlined}
                placeholder={t('placeholder.comment') + '...'}
                value={editCommentContent}
                onChange={(e) => setEditCommentContent(e.target.value)}
                onKeyDown={(e) => handleEditKeyPress(e)}
              />
            </div>
          ) : (
            <div className="tw-flex-grow tw-text-sm tw-max-w-[calc(100%-40px)]">
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
                <span className="tw-font-semibold">
                  {comment.users?.fullname || comment.staffs?.fullname}
                </span>
                <span className="tw-w-0.5 tw-h-0.5 tw-rounded-full tw-bg-secondary-70"></span>
                <span className="tw-text-secondary-70 tw-font-light">
                  {dayjs(comment.updated_at).format('HH:mm DD/MM/YYYY')}
                </span>
              </div>
              <div className="tw-font-light tw-mb-1 tw-break-words">{comment.comment}</div>
              <div className="tw-flex tw-items-center tw-justify-between">
                <div>
                  <div
                    className="tw-text-primary-main tw-cursor-pointer tw-flex tw-items-center tw-gap-1"
                    onClick={() => {
                      if (!isAuth) {
                        return toast.error(t('message.unauthorized'));
                      }
                      onShowReplyInput(comment);
                      if (!isChild) {
                        setCommentToExpand(comment as VideoReelCommentModel);
                        setIsExpandingReplies(true);
                      }
                    }}
                  >
                    <Icon
                      className="tw-w-[14px]"
                      icon={<CommentIcon width={14} height={14} />}
                    />
                    {t('button.reply')}
                  </div>
                </div>
                <div>
                  {isOwner() && (
                    <span
                      className="tw-text-primary-main tw-cursor-pointer"
                      onClick={() => showEditComment()}
                    >
                      {t('button.edit')}
                    </span>
                  )}
                  {isOwner() && (
                    <span
                      className="tw-ml-4 tw-text-[#333333] tw-cursor-pointer"
                      onClick={() => setIsShowConfirmDelete(true)}
                    >
                      {t('button.delete')}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {(comment as VideoReelCommentModel).reply_comment_count > 0 && (
          <>
            <div
              className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
              onClick={() =>
                handleToggleShowReply(comment as VideoReelCommentModel)
              }
            >
              <div className="tw-w-6 tw-h-[2px] tw-bg-gray-41"></div>
              <span className="tw-text-sm tw-text-gray-41">
                {isExpandingReplies
                  ? t('label.hide-replies')
                  : t('label.number-replies', {
                      value: (comment as VideoReelCommentModel)
                        .reply_comment_count,
                    })}
              </span>
            </div>
            {isExpandingReplies &&
              Number(
                (comment as VideoReelCommentModel).reel_reply_comments?.length
              ) > 0 && (
                <div className="tw-pl-6 tw-pt-2 tw-flex tw-flex-col-reverse">
                  {(comment as VideoReelCommentModel).reel_reply_comments?.map(
                    (replyComment: VideoReelReplyCommentModel) => {
                      return (
                        <Comment
                          key={`comment-${replyComment.id}`}
                          t={t}
                          profile={profile}
                          isAuth={isAuth}
                          comment={replyComment}
                          isChild={true}
                          isShowReplyInput={isShowReplyInput}
                          replyCommentContent={replyCommentContent}
                          setReplyCommentContent={setReplyCommentContent}
                          setReplyCommentInput={setReplyCommentInput}
                          editCommentInput={editCommentInput}
                          setEditCommentInput={setEditCommentInput}
                          editCommentContent={editCommentContent}
                          setEditCommentContent={setEditCommentContent}
                          onShowReplyInput={onShowReplyInput}
                          onSetReplies={onSetReplies}
                          onSendReplyComment={onSendReplyComment}
                        ></Comment>
                      );
                    }
                  )}
                </div>
              )}
          </>
        )}
        {!isChild && isShowReplyInput && (
          <div className={`transition-height tw-pl-6 tw-h-auto tw-mt-2`}>
            <div className="tw-flex tw-items-center tw-gap-2">
              <Avatar
                src={profile?.avatar}
                className="tw-w-8 tw-h-8"
                rounded="tw-rounded-full"
              />
              <div className="tw-flex-grow tw-relative">
                <Input
                  containerClassName="tw-rounded-full tw-px-4"
                  className="tw-text-sm"
                  variant={InputVariant.Outlined}
                  placeholder={t('placeholder.reply-comment') + '...'}
                  value={replyCommentContent}
                  onChange={(e) => setReplyCommentContent(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e)}
                />
                <div className="tw-absolute tw-r-0 tw-b-0"></div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isShowConfirmDelete && (
        <ModalCustom
          className="tw-w-[360px]"
          onClose={() => setIsShowConfirmDelete(false)}
        >
          <ModalHeader>
            <div className="tw-font-bold tw-text-lg tw-text-center">
              Xóa bình luận
            </div>
          </ModalHeader>
          <ModalBody>
            <p>Bạn có chắn chắn muốn xóa bình luận này?</p>
          </ModalBody>
          <ModalFooter>
            <div className="tw-flex tw-justify-end tw-gap-3">
              <Button
                variant={ButtonVariant.Outlined}
                className="tw-border-secondary-70 tw-text-secondary-70"
                onClick={() => setIsShowConfirmDelete(false)}
              >
                Hủy
              </Button>
              <Button
                variant={ButtonVariant.Standard}
                className="tw-bg-red-600/70 tw-border-red-600/70"
                onClick={handleDeleteComment}
              >
                Xóa
              </Button>
            </div>
          </ModalFooter>
        </ModalCustom>
      )}
    </>
  );
};

export default React.memo(Comment);
